import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDuration, mediaUrl } from "@/lib/format";
import { CategoryThumb } from "@/components/CategoryThumb";
import { VerifiedShield } from "@/components/VerifiedShield";
import { Clock, MapPin, ArrowRight, ArrowLeft, Store, Globe, Instagram, Facebook, Twitter, Linkedin, Youtube, Music2, Award } from "lucide-react";

const SOCIAL_META = {
  website: { icon: Globe, label: "Website" },
  instagram: { icon: Instagram, label: "Instagram" },
  facebook: { icon: Facebook, label: "Facebook" },
  x: { icon: Twitter, label: "X" },
  linkedin: { icon: Linkedin, label: "LinkedIn" },
  tiktok: { icon: Music2, label: "TikTok" },
  youtube: { icon: Youtube, label: "YouTube" },
};
const withHttp = (u) => (/^https?:\/\//i.test(u) ? u : `https://${u}`);

export default function ProviderProfile() {
  const { providerId } = useParams();
  const { user } = useAuth();
  const isOwner = user && user.id === providerId;
  const [profile, setProfile] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [badges, setBadges] = useState([]);

  useEffect(() => {
    api.get(`/providers/${providerId}/profile`)
      .then((r) => setProfile(r.data))
      .catch(() => setNotFound(true));
    api.get(`/providers/${providerId}/badges`).then((r) => setBadges(r.data)).catch(() => {});
  }, [providerId]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-24 text-center" data-testid="profile-notfound">
        <h1 className="font-display text-3xl font-semibold">Provider not found</h1>
        <p className="text-muted-foreground mt-2">This provider profile isn't available.</p>
        <Link to="/browse" className="text-primary underline mt-4 inline-block">Browse services</Link>
      </div>
    );
  }
  if (!profile) return <div className="py-24 text-center text-muted-foreground">Loading…</div>;

  const memberYear = profile.member_since ? new Date(profile.member_since).getFullYear() : null;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="provider-profile">
      {isOwner && (
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4" />Back to dashboard
        </Link>
      )}
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-5 border-b border-border pb-8">
        <div className="w-20 h-20 rounded-sm bg-muted overflow-hidden flex items-center justify-center shrink-0 border border-border">
          {profile.logo_url
            ? <img src={mediaUrl(profile.logo_url)} alt={profile.name} className="w-full h-full object-cover" data-testid="profile-logo" />
            : <Store className="w-8 h-8 text-muted-foreground" />}
        </div>
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-semibold" data-testid="profile-name">{profile.name}</h1>
            {profile.identity_verified && <VerifiedShield size="lg" withLabel testid="profile-verified" />}
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-muted-foreground">
            {profile.city && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{profile.city}</span>}
            {memberYear && <span>On BookdHUB since {memberYear}</span>}
            <span className="flex items-center gap-1">{profile.services.length} service{profile.services.length === 1 ? "" : "s"}</span>
          </div>
        </div>
      </div>

      {/* About + socials */}
      {(profile.bio || Object.keys(profile.socials || {}).length > 0) && (
        <div className="mt-8" data-testid="profile-about">
          {profile.bio && <p className="text-base text-neutral-700 leading-relaxed whitespace-pre-line max-w-3xl">{profile.bio}</p>}
          {Object.keys(profile.socials || {}).length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {Object.entries(profile.socials).map(([k, url]) => {
                const meta = SOCIAL_META[k];
                if (!meta || !url) return null;
                const Icon = meta.icon;
                return (
                  <a key={k} href={withHttp(url)} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm border border-border rounded-sm px-3 py-1.5 hover:border-secondary hover:text-primary transition-all"
                    data-testid={`profile-social-${k}`}>
                    <Icon className="w-4 h-4" />{meta.label}
                  </a>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Badges / achievements */}
      {badges.length > 0 && (
        <div className="mt-10" data-testid="profile-badges">
          <h2 className="font-display text-2xl font-medium mb-4">Achievements</h2>
          <div className="flex flex-wrap gap-3">
            {badges.map((b) => (
              <div key={b.key} className="flex items-center gap-2 border border-primary/30 bg-primary/5 rounded-sm px-3 py-2" data-testid={`profile-badge-${b.key}`}>
                <Award className="w-5 h-5 text-primary shrink-0" />
                <div>
                  <div className="text-sm font-medium">{b.title}</div>
                  <div className="text-xs text-muted-foreground">{b.subtitle}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Services */}
      <h2 className="font-display text-2xl font-medium mt-10 mb-4">Services</h2>
      {profile.services.length === 0 ? (
        <p className="text-muted-foreground">This provider hasn't published any services yet.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {profile.services.map((s) => (
            <Link key={s.id} to={`/service/${s.id}`} data-testid={`profile-service-${s.id}`}
              className="group border border-border rounded-sm overflow-hidden bg-white hover:border-secondary transition-all">
              <CategoryThumb imageUrl={s.image_url} category={s.category} testid={`profile-image-${s.id}`}
                imgClassName="w-full h-40 object-cover" tileClassName="w-full h-40" />
              <div className="p-4">
                <span className="text-[10px] uppercase tracking-wider font-bold text-primary bg-primary/10 rounded-sm px-1.5 py-0.5">{s.category}</span>
                <h3 className="font-display text-lg font-medium mt-2 leading-tight flex items-center gap-1.5">{s.title}{profile.identity_verified && <VerifiedShield testid={`profile-service-verified-${s.id}`} />}</h3>
                <div className="flex items-center justify-between mt-3">
                  <span className="font-display text-lg font-semibold">${Number(s.price).toFixed(2)}</span>
                  <span className="flex items-center gap-1 text-sm text-muted-foreground"><Clock className="w-4 h-4" />{formatDuration(s.duration_minutes)}</span>
                </div>
                <span className="inline-flex items-center gap-1 text-sm text-primary mt-3 group-hover:gap-2 transition-all" data-testid={`profile-service-cta-${s.id}`}>{isOwner ? "View service" : "Book now"}<ArrowRight className="w-4 h-4" /></span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
